import React from "react";
import { useSelector } from "react-redux";
import Badge from "@mui/material/Badge";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloudDoneIcon from "@mui/icons-material/CloudDone";
import CloudOffIcon from "@mui/icons-material/CloudOff";
import ErrorIcon from "@mui/icons-material/Error";
import Tooltip from "@mui/material/Tooltip";
import { useIntl } from "react-intl";
import {
  selectAllItems,
  selectIsLoading,
} from "@/modules/ticket/ticketQueue/ticketQueueSlice";

interface QueueStatusBadgeProps {
  onClick?: () => void;
}

/**
 * Badge component showing queue sync status in navigation
 *
 * Visual states:
 * - No items: CloudDone (green)
 * - Pending items: CloudUpload with badge count (blue)
 * - Syncing: CircularProgress spinner
 * - Error items: Error icon with badge count (red)
 * - Offline: CloudOff (grey)
 */
const QueueStatusBadge: React.FC<QueueStatusBadgeProps> = ({ onClick }) => {
  const intl = useIntl();
  const items = useSelector(selectAllItems);
  const isLoading = useSelector(selectIsLoading);
  const isOnline = navigator.onLine;

  const pendingCount = items.filter((item) => item.status === "pending").length;
  const errorCount = items.filter((item) => item.status === "error").length;
  const sendingCount = items.filter((item) => item.status === "sending").length;

  // Always render badge for testing, even if queue is empty and online

  // Determine icon and color
  let icon: React.ReactNode;
  let color: "primary" | "secondary" | "error" | "default" = "default";
  let tooltip = "";

  if (!isOnline) {
    icon = <CloudOffIcon />;
    color = "default";
    tooltip = intl.formatMessage(
      { id: "queue.status.offline", defaultMessage: "Offline" },
      { count: pendingCount },
    );
  } else if (isLoading || sendingCount > 0) {
    icon = <CircularProgress size={24} color="primary" />;
    tooltip = intl.formatMessage(
      { id: "queue.status.syncing", defaultMessage: "Syncing {count} item(s)" },
      { count: sendingCount || pendingCount },
    );
  } else if (errorCount > 0) {
    icon = <ErrorIcon />;
    color = "error";
    tooltip = intl.formatMessage(
      { id: "queue.status.error", defaultMessage: "{count} item(s) failed" },
      { count: errorCount },
    );
  } else if (pendingCount > 0) {
    icon = <CloudUploadIcon />;
    color = "primary";
    tooltip = intl.formatMessage(
      {
        id: "queue.status.pending",
        defaultMessage: "{count} item(s) pending",
      },
      { count: pendingCount },
    );
  } else {
    icon = <CloudDoneIcon />;
    color = "primary";
    tooltip = intl.formatMessage({
      id: "queue.status.synced",
      defaultMessage: "All synced",
    });
  }

  const badgeCount = errorCount > 0 ? errorCount : pendingCount;

  return (
    <Tooltip title={tooltip}>
      <IconButton onClick={onClick} color={color} size="large">
        {badgeCount > 0 ? (
          <Badge
            badgeContent={badgeCount}
            color={errorCount > 0 ? "error" : "secondary"}
            overlap="rectangular"
          >
            {icon}
          </Badge>
        ) : (
          icon
        )}
      </IconButton>
    </Tooltip>
  );
};

export default QueueStatusBadge;
